import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import gql from 'graphql-tag';
import { Card } from '@zillow/constellation';

import type { Property } from '../types/@zillow-types/pongo';
import {
    ExperienceMessage,
    OmpV2WebUpsellCard,
    OmpV2WebEmphasizedUpsellCard,
} from '@zillow/experience-messaging-client';
import type { OmpV2Result as ExperienceOmpV2Result } from '@zillow/experience-messaging-client';

interface OmpV2Component {
    [key: string]: unknown;
    __typename?: string;
}

interface OmpV2PlacementDecision {
    selectedMessage?: {
        component?: OmpV2Component;
    };
}

export interface OmpV2Result {
    placementGroupDecision?: {
        placementDecisions?: OmpV2PlacementDecision[];
    };
}

interface OmpHomeWorthProperty extends Property {
    ompV2Message?: OmpV2Result;
}

// Only aliased fields we actually request in the fragments
const COMPONENT_ALIAS_MAPPINGS: Record<string, Record<string, string>> = {
    OmpV2WebEmphasizedUpsellCard: {
        header: 'emphasizedHeader',
        body: 'emphasizedBody',
    },
};

/**
 * Maps aliased component fields back to their expected names for OMP v2 results.
 * This avoids GraphQL field type conflicts while keeping downstream consumers
 * on the canonical field names (header/body/etc).
 */
export const mapOmpV2ComponentAlias = (ompV2Result?: OmpV2Result) => {
    const decisions = ompV2Result?.placementGroupDecision?.placementDecisions;
    if (!decisions) {
        return ompV2Result;
    }

    const cloned = cloneDeep(ompV2Result);
    if (!cloned) {
        return ompV2Result;
    }

    const targetDecisions = cloned.placementGroupDecision?.placementDecisions;
    if (!Array.isArray(targetDecisions)) {
        return cloned;
    }

    targetDecisions.forEach((decision) => {
        const comp = decision.selectedMessage?.component;
        // eslint-disable-next-line no-underscore-dangle
        const mapping = comp?.__typename ? COMPONENT_ALIAS_MAPPINGS[comp.__typename] : undefined;
        if (!mapping) {
            return;
        }

        const compRecord = comp as Record<string, unknown>;
        Object.entries(mapping).forEach(([targetField, sourceField]) => {
            const sourceValue = compRecord[sourceField];
            if (sourceValue !== undefined) {
                compRecord[targetField] = sourceValue;
            }
        });
    });

    return cloned;
};

interface OmpHomeWorthUpsellProps {
    property: OmpHomeWorthProperty;
}

const OmpHomeWorthUpsell = ({ property }: OmpHomeWorthUpsellProps) => {
    const { ompV2Message } = property;
    const triggerLocation = 'how_much_is_my_home_worth';
    const triggerObject = 'top';
    const triggerSource = 'how_much_is_my_home_worth|top';
    const mappedOmpV2Message = mapOmpV2ComponentAlias(ompV2Message);

    if (!mappedOmpV2Message) {
        return null;
    }

    return (
        <Card
            data-testid="hmimhw-omp-upsell-card"
            cardType="secondary"
            padding="sm"
            cardElevation={0}
        >
            <ExperienceMessage
                result={mappedOmpV2Message as ExperienceOmpV2Result}
                clickstreamTrigger={{
                    triggerLocation,
                    triggerObject,
                    triggerSource,
                    triggerObjectIsSecondary: false,
                }}
                placementName="HMIMHW_TOP_SLOT"
                supportedComponents={{
                    OmpV2WebUpsellCard,
                    OmpV2WebEmphasizedUpsellCard,
                }}
            />
        </Card>
    );
};

export const OMP_UPSELL_FRAGMENT_V2 = gql`
    fragment OmpUpsellTopSlotV2_property on Property {
        ompV2Message(
            placementGroup: HMIMHW_TOP
            config: {
                placementSupportedComponents: [
                    {
                        placementName: HMIMHW_TOP_SLOT
                        supportedComponents: [OmpV2WebEmphasizedUpsellCard, OmpV2WebUpsellCard]
                    }
                ]
            }
        ) {
            placementGroupDecision {
                placementGroup
                experienceType
                experienceName
                placementDecisions {
                    placementName
                    selectedMessage {
                        id
                        name
                        component {
                            __typename
                            ... on OmpV2WebEmphasizedUpsellCard {
                                emphasizedHeader: header
                                emphasizedBody: body {
                                    beforeText
                                    afterText
                                    tooltip {
                                        trigger
                                        title
                                        subTitle
                                        content
                                        actionText
                                        actionUrl
                                    }
                                }
                                primarySection {
                                    label {
                                        ... on OmpV2WebEmphasizedUpsellCardValueSectionText {
                                            text
                                            subText
                                            fontColor
                                            fontType
                                        }
                                        ... on OmpV2WebEmphasizedUpsellCardValueSectionTooltip {
                                            trigger
                                            title
                                            subTitle
                                            content
                                            actionText
                                            actionUrl
                                        }
                                    }
                                    value {
                                        ... on OmpV2WebEmphasizedUpsellCardValueSectionText {
                                            text
                                            subText
                                            fontColor
                                            fontType
                                        }
                                        ... on OmpV2WebEmphasizedUpsellCardValueSectionTooltip {
                                            trigger
                                            title
                                            subTitle
                                            content
                                            actionText
                                            actionUrl
                                        }
                                    }
                                    hasDivider
                                }
                                secondarySection {
                                    label {
                                        ... on OmpV2WebEmphasizedUpsellCardValueSectionText {
                                            text
                                            subText
                                            fontColor
                                            fontType
                                        }
                                        ... on OmpV2WebEmphasizedUpsellCardValueSectionTooltip {
                                            trigger
                                            title
                                            subTitle
                                            content
                                            actionText
                                            actionUrl
                                        }
                                    }
                                    value {
                                        ... on OmpV2WebEmphasizedUpsellCardValueSectionText {
                                            text
                                            subText
                                            fontColor
                                            fontType
                                        }
                                        ... on OmpV2WebEmphasizedUpsellCardValueSectionTooltip {
                                            trigger
                                            title
                                            subTitle
                                            content
                                            actionText
                                            actionUrl
                                        }
                                    }
                                    hasDivider
                                }
                                tertiarySection {
                                    label {
                                        ... on OmpV2WebEmphasizedUpsellCardValueSectionText {
                                            text
                                            subText
                                            fontColor
                                            fontType
                                        }
                                        ... on OmpV2WebEmphasizedUpsellCardValueSectionTooltip {
                                            trigger
                                            title
                                            subTitle
                                            content
                                            actionText
                                            actionUrl
                                        }
                                    }
                                    value {
                                        ... on OmpV2WebEmphasizedUpsellCardValueSectionText {
                                            text
                                            subText
                                            fontColor
                                            fontType
                                        }
                                        ... on OmpV2WebEmphasizedUpsellCardValueSectionTooltip {
                                            trigger
                                            title
                                            subTitle
                                            content
                                            actionText
                                            actionUrl
                                        }
                                    }
                                    hasDivider
                                }
                                primaryCTA {
                                    ctaUrl
                                    ctaText
                                    ctaButtonStyle
                                    ctaSubText
                                }
                                isPrimaryCTAFluid
                                isNarrowContainer
                            }
                            ... on OmpV2WebUpsellCard {
                                header
                                body
                                primaryCTA {
                                    ctaUrl
                                    ctaText
                                    ctaButtonStyle
                                    ctaSubText
                                }
                                secondaryCTA {
                                    ctaUrl
                                    ctaText
                                    ctaButtonStyle
                                    ctaSubText
                                }
                                imageMedia {
                                    src
                                    alt
                                    width
                                    height
                                }
                                advertisementText
                                advertisementMedia {
                                    src
                                    alt
                                    width
                                    height
                                }
                                hasBorder
                                dismissible
                                backgroundColor
                                mlsText
                            }
                        }
                    }
                    eventMetadata {
                        exposureBlock {
                            randomizationKey
                            keyTypeCd
                            decisionToken
                            treatments
                            assignmentServiceCd
                            extendedInfo {
                                key
                                value
                            }
                        }
                        nbaBlock {
                            isNbaProcessed
                            surfaceId
                            position
                            joiningUuid
                            serviceErrorInd
                            serviceErrorTxt
                            selectedActionPositions {
                                key
                                value
                            }
                            userImpressionReportedInd
                            fallBackActionRankingUsedInd
                            selectedFallBackActions {
                                key
                                value
                            }
                        }
                        ompV2Block {
                            placementName
                            placementGroup
                            assignedExperienceType
                            selectedExperienceType
                            selectedExperienceName
                            selectedExperienceTrial
                            selectedExperienceTrialAssignment
                            selectedNbaMessage
                            selectedMessage
                            selectedMessageAttributions
                            qualifiedExperiencesMultivariateName
                            qualifiedExperiencesDefaultName
                            qualifiedExperiencesNbaName
                        }
                    }
                }
            }
        }
    }
`;

OmpHomeWorthUpsell.fragments = {
    // OmpHomeWorthUpsell fields can be found in HowMuchIsMyHomeWorthReviewQuery
    property: gql`
        fragment OmpHomeWorthUpsell_property on Property {
            zpid
            ...OmpUpsellTopSlotV2_property
        }
        ${OMP_UPSELL_FRAGMENT_V2}
    `,
};

export default OmpHomeWorthUpsell;
